import { useState, useMemo, useEffect, type ReactNode } from "react";
import type { CalendarEvent } from "../../prop-firm-manager/PropFirmCalendar";
import { Star, Lock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../client/components/ui/tooltip";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../client/components/ui/dialog";
import { Button } from "../../client/components/ui/button";
import { cn } from "../../client/utils";
import useTranslation from "../../client/hooks/useTranslation";
import { useIsDark } from "../../client/components/ui/card-section-header";
import type { WidgetKey, LayoutSnapshot } from "./types";
import { WIDGET_REGISTRY, WIDGET_CATEGORIES, type WidgetCategoryKey, isWidgetLocked } from "./registry";
import { DashboardDataProvider } from "./DashboardDataContext";
import { WIDGET_COMPONENTS } from "./GlobalDashboardGrid";

export interface AddWidgetPickerProps {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  layout: LayoutSnapshot;
  /** Number of free cells available at the target slot (= colSpan max possible). */
  maxColSpan: number;
  /** The row where the widget will be placed (1-indexed). */
  rowStart: number;
  /** The column where the widget will be placed (1-indexed). */
  colStart: number;
  scope?: "dashboard" | "pfm" | "custom-page" | "journal" | "analytics" | "sessions" | "discipline";
  /** Force opening on a specific category tab */
  initialCategory?: WidgetCategoryKey;
  onPick: (widgetKey: WidgetKey) => void;
  onUpgradeClick?: () => void;
  isFreePlan?: boolean;
  hasMentor?: boolean;
  /** Topbar filter pin support — only used when scope="custom-page" */
  topbarPinState?: {
    /** Current serialized value per topbar widget key */
    values: Partial<Record<WidgetKey, string>>;
    /** Saved default value per topbar widget key (from slot.defaultFilterValue) */
    defaults: Partial<Record<WidgetKey, string | null>>;
    onPin: (widgetKey: WidgetKey, value: string) => void;
  };
}

function camelize(key: string): string {
  return key.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

const MOCK_MONTHLY_STATS = [
  { month: "2026-04", trades: 18, wins: 11, losses: 7, totalPnl: 420, winRate: 61, profitFactor: 1.6 },
  { month: "2026-05", trades: 42, wins: 26, losses: 16, totalPnl: 1234, winRate: 64, profitFactor: 1.9 },
];
const MOCK_HEATMAP: Array<{ hour: number; day: number; pnl: number; count: number; winRate: number }> = Array.from({ length: 35 }, (_, i) => ({
  hour: (i % 7) + 9, day: Math.floor(i / 7), pnl: (i % 3 === 0 ? -1 : 1) * (i + 1) * 12, count: (i % 4) + 1, winRate: 50 + (i % 30),
}));

const MOCK_ROUTINES = [
  { id: "r1", name: "Revue pré-marché", frequency: "daily" },
  { id: "r2", name: "Vérifier niveaux clés", frequency: "daily" },
  { id: "r3", name: "Journal de trading", frequency: "weekly" },
];
const MOCK_GOALS = [
  { goal: { id: "g1", title: "Profit Factor > 1.8" }, progress: 78, daysLeft: 12 },
  { goal: { id: "g2", title: "Win Rate 65%" }, progress: 45, daysLeft: 5 },
];
const MOCK_ACCOUNTS = [
  { id: "a1", name: "FTMO 50K", status: "pa", initialBalance: 50000, currentBalance: 51234, isActive: true, strategies: [], effectiveTemplate: { name: "FTMO", color: "#1a1a2e", textColor: "#ffffff" }, activeTier: null, challengeProgress: null, bufferStatus: null, ruleViolations: {} },
  { id: "a2", name: "TopStep 100K", status: "challenge", initialBalance: 100000, currentBalance: 101820, isActive: true, strategies: [], effectiveTemplate: { name: "TopStep", color: "#003087", textColor: "#ffffff" }, activeTier: null, challengeProgress: { ratio: 0.62, profit: 1820, targetAmount: 3000, targetPercent: null }, bufferStatus: null, ruleViolations: {} },
];

function makeMockData(t: any, isDark: boolean, previewChartHeight?: number) {
  const now = Date.now();
  return {
    trades: [], routines: MOCK_ROUTINES, goals: MOCK_GOALS.map(g => g.goal), accounts: MOCK_ACCOUNTS, groups: [], strategies: [], sessions: [
      { id: "s1", startedAt: new Date(Date.now() - 86400000 * 1), endedAt: new Date(Date.now() - 86400000 * 1 + 7200000), actualDuration: 120, isActive: false },
      { id: "s2", startedAt: new Date(Date.now() - 86400000 * 3), endedAt: new Date(Date.now() - 86400000 * 3 + 5400000), actualDuration: 90, isActive: false },
      { id: "s3", startedAt: new Date(Date.now() - 86400000 * 7), endedAt: new Date(Date.now() - 86400000 * 7 + 3600000), actualDuration: 60, isActive: false },
    ],
    metrics: { totalPnl: 1234.56, totalTrades: 42, winRate: 62.5, profitFactor: 1.87, averageWin: 312, averageLoss: -167, winningTrades: 26, losingTrades: 16, largestWin: 580, largestLoss: -210, maxDrawdown: 4.2, sharpeRatio: 1.4, expectancy: 29.4 },
    statsTrades: [], statsTradesList: [],
    equityCurveData: { points: Array.from({ length: 10 }, (_, i) => ({ x: now - 86400000 * (9 - i), y: i * 137 })), totalPnl: 1234, xMin: now - 86400000 * 9, xMax: now },
    accountEvents: [], fundedEvents: [], payoutEvents: [], violationsByDate: new Map(),
    calendarEvents: [
      { date: new Date(2026, 4, 8),  type: "payout",   amount: 1600, currency: "USD", label: "FTMO 50K" },
      { date: new Date(2026, 4, 15), type: "funded",   amount: 0,    currency: "USD", label: "TopStep funded" },
      { date: new Date(2026, 4, 22), type: "payout",   amount: 800,  currency: "USD", label: "E8 100K" },
      { date: new Date(2026, 4, 5),  type: "expense",  amount: -99,  currency: "USD", label: "Frais FTMO" },
      { date: new Date(2026, 4, 1),  type: "recurring",amount: -49,  currency: "USD", label: "Abonnement" },
    ] as CalendarEvent[],
    setCalendarMonth: () => {},
    pendingRoutines: MOCK_ROUTINES, topGoals: MOCK_GOALS,
    dateRange: "30d" as any, accountFilter: { type: "all" as const }, strategyIds: [],
    statsStartDate: null, statsEndDate: null,
    setSelectedDay: () => {}, handleCompleteRoutine: () => {}, setSelectedMonth: () => {},
    onAddTrade: () => {}, onPromoteToFunded: () => {}, onOpenPlanModal: () => {},
    isDark, isFreePlan: false, readOnly: false, t,
    chartMode: "pnl" as const, setChartMode: () => {},
    previewChartHeight,
    analyticsData: {
      symbolStats: [{ symbol: "MNQ", trades: 28, wins: 18, losses: 10, totalPnl: 820, winRate: 64, averagePnl: 29 }],
      monthlyStats: MOCK_MONTHLY_STATS,
      cumulativePnl: [{ x: Date.now() - 86400000 * 30, y: 0 }, { x: Date.now(), y: 1234 }],
      pnlDistribution: [{ range: "-100/-50", count: 3 }, { range: "0/50", count: 12 }, { range: "50/100", count: 8 }],
      rollingWinRate: [{ tradeIndex: 1, winRate: 60, date: "2026-04-01" }, { tradeIndex: 20, winRate: 65, date: "2026-05-01" }],
      heatmapData: MOCK_HEATMAP,
      timeSlotStats: [],
      holdDurationData: [],
      tagStats: [],
      behavioralBiases: { fomoIndicator: 12.5, lossAversionRatio: 1.4, revengeTradingCount: 3, overconfidenceScore: 0.6 },
      sessionStats: {
        timeInSessionBuckets: [],
        moodStats: [],
        durationBuckets: [],
        outsideSession: {
          tradeCount: 17,
          winCount: 15,
          lossCount: 2,
          winRate: 88,
          totalPnl: 214,
          avgPnl: 13,
          profitFactor: 4.43,
          pctOfTotalTrades: 5,
          pctOfTotalPnl: 5,
        },
        insight: { type: "timeBucket", bucketLabel: "0–30 min", pct: 53 },
        hasEnoughData: true,
      },
      pipStats: { tradeCount: 28, avgPips: 12, avgPipsWin: 22, avgPipsLoss: -8, totalPipsWon: 396, totalPipsLost: -128, pipWinLossRatio: 2.75, bestTradePips: 48, worstTradePips: -18 },
      pipBuckets: [
        { label: "-30/-20", count: 4, wins: 1, losses: 3, winRate: 25, avgPnl: -180 },
        { label: "-20/-10", count: 7, wins: 2, losses: 5, winRate: 29, avgPnl: -95 },
        { label: "-10/0",   count: 5, wins: 1, losses: 4, winRate: 20, avgPnl: -42 },
        { label: "0/10",    count: 6, wins: 3, losses: 3, winRate: 50, avgPnl: 38 },
        { label: "10/20",   count: 8, wins: 6, losses: 2, winRate: 75, avgPnl: 112 },
        { label: "20/30",   count: 5, wins: 4, losses: 1, winRate: 80, avgPnl: 198 },
        { label: "30/50",   count: 3, wins: 3, losses: 0, winRate: 100, avgPnl: 340 },
      ],
      pipBySymbol: [{ symbol: "MNQ", trades: 28, avgPips: 12, totalPips: 504, wins: 18, winRate: 64, avgPnl: 29 }],
      calmarRatio: 1.2,
      recoveryFactor: 2.1,
      sizingData: Array.from({ length: 20 }, (_, i) => ({ tradeIndex: i + 1, absSize: 100 + (i % 5) * 40, pnl: (i % 3 === 0 ? -1 : 1) * (40 + i * 6), date: "2026-05-01", isWin: i % 3 !== 0 })),
      longShort: { longPnl: 820, shortPnl: 414, longCount: 18, shortCount: 10, longWinRate: 66, shortWinRate: 55, longAvgPnl: 45, shortAvgPnl: 41 },
      dayOfWeek: {
        dayStats: [
          { day: "Monday", dayShort: "Mon", pnl: 320, count: 8, avgPnl: 40 },
          { day: "Tuesday", dayShort: "Tue", pnl: -90, count: 5, avgPnl: -18 },
          { day: "Wednesday", dayShort: "Wed", pnl: 540, count: 9, avgPnl: 60 },
          { day: "Thursday", dayShort: "Thu", pnl: 180, count: 6, avgPnl: 30 },
          { day: "Friday", dayShort: "Fri", pnl: 284, count: 7, avgPnl: 41 },
        ],
        bestDay: { day: "Wednesday", dayShort: "Wed", pnl: 540, count: 9, avgPnl: 60 },
        worstDay: { day: "Tuesday", dayShort: "Tue", pnl: -90, count: 5, avgPnl: -18 },
      },
      streakData: {
        longestLoss: { type: "loss" as const, length: 3, totalPnl: -210 },
        longestWin: { type: "win" as const, length: 6, totalPnl: 720 },
        currentStreak: { type: "win" as const, length: 2, totalPnl: 140 },
        drawdownScore: 75, drawupScore: 60, drawdownMgmtTotal: 4, drawdownMgmtOk: 3, drawupMgmtTotal: 3, drawupMgmtOk: 2,
      },
      consistencyScore: { total: 72, winRateStability: 18, riskConsistency: 20, revengeScore: 17, drawdownMgmt: 17 },
      netDailyPnl: [
        { date: "2026-05-01", pnl: 120 }, { date: "2026-05-02", pnl: -60 }, { date: "2026-05-05", pnl: 240 },
        { date: "2026-05-06", pnl: 80 }, { date: "2026-05-07", pnl: -40 }, { date: "2026-05-08", pnl: 180 },
      ],
      drawdownCurve: Array.from({ length: 12 }, (_, i) => ({ x: Date.now() - 86400000 * (11 - i), y: -(i % 4) * 60 })),
    },
  };
}

const PFM_WIDGET_KEYS = new Set<WidgetKey>([
  "propfirm-kpis", "propfirm-month-recap", "propfirm-reporting",
  "propfirm-accounts", "propfirm-payouts-list", "propfirm-filters", "propfirm-accounts-table",
  "propfirm-global-charges", "propfirm-upcoming-charges", "propfirm-wallet-balance", "propfirm-wallet-transfers", "propfirm-wallet-withdrawals",
]);

export const TOPBAR_WIDGET_KEYS = new Set<WidgetKey>([
  "topbar-date-filter", "topbar-account-filter", "topbar-strategy-filter",
  "topbar-add-trade", "topbar-add-payout", "topbar-pfm-filter", "topbar-phase-filter",
  "topbar-symbol-filter", "topbar-side-filter", "topbar-status-filter", "topbar-pnl-filter", "topbar-tags-filter",
]);



function PropFirmWidgetPreview({ widgetKey, height }: { widgetKey: WidgetKey; height: number }) {
  // Token-based preview colors (theme-aware).
  const teal = "hsl(var(--primary))";
  const red = "hsl(var(--edgely-pnl-negative))";
  const gold = "hsl(var(--warning))";
  const blue = "hsl(var(--chart-axis))";

  const content: Record<string, ReactNode> = {
    // 6 KPI tiles in a row (grid-cols-6), each: label uppercase + big mono value
    "propfirm-kpis": (
      <div className="grid grid-cols-6 gap-2 w-full">
        {[
          ["Total payouts", "+$3 200", teal],
          ["Dépenses", "-$480", red],
          ["Profit net", "+$2 720", teal],
          ["ROI global", "+568%", teal],
          ["Comptes actifs", "4", undefined],
          ["Meilleur ROI", "+820%", teal],
        ].map(([label, val, color]) => (
          <div key={label as string} className="rounded-lg border bg-card p-3 flex flex-col justify-center">
            <p className="text-[9px] font-semibold tracking-widest uppercase mb-1" style={{ color: blue }}>{label as string}</p>
            <p className="text-sm font-bold font-mono leading-none" style={{ color: (color as string) ?? "inherit" }}>{val as string}</p>
          </div>
        ))}
      </div>
    ),
    // Full calendar widget with week headers + day cells colored by payout/event
    "propfirm-calendar": (
      <div className="rounded-lg border bg-card h-full flex flex-col p-2 gap-1">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs font-semibold">Juin 2026</span>
          <div className="flex gap-1">
            <div className="w-5 h-5 rounded bg-muted/50" />
            <div className="w-5 h-5 rounded bg-muted/50" />
          </div>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center">
          {["L","M","M","J","V","S","D"].map((d, i) => (
            <span key={i} className="text-[9px] font-medium text-muted-foreground py-0.5">{d}</span>
          ))}
          {Array.from({ length: 30 }, (_, i) => {
            const events = [1, 5, 8, 12, 15, 19, 22, 26];
            const payout = [8, 22];
            const isPayout = payout.includes(i);
            const isEvent = events.includes(i);
            return (
              <div
                key={i}
                className="rounded text-[9px] py-0.5 text-center"
                style={{
                  background: isPayout ? "hsl(var(--primary) / 0.13)" : isEvent ? "#3D6EFF15" : "transparent",
                  color: isPayout ? teal : isEvent ? "#3D6EFF" : "inherit",
                  fontWeight: isPayout || isEvent ? 600 : undefined,
                }}
              >
                {i + 1}
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 mt-1">
          <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: teal }} /><span className="text-[9px] text-muted-foreground">Payout</span></div>
          <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block bg-blue-500" /><span className="text-[9px] text-muted-foreground">Événement</span></div>
        </div>
      </div>
    ),
    // Month recap: scrollable list of events for selected month
    "propfirm-month-recap": (
      <div className="rounded-lg border bg-card h-full flex flex-col">
        <div className="px-3 pt-3 pb-2 border-b">
          <p className="text-xs font-semibold">Juin 2026</p>
        </div>
        <div className="flex flex-col gap-1 p-2 overflow-hidden">
          {[
            ["08 juin", "Payout reçu — FTMO 50K", "+$1 600", teal],
            ["15 juin", "Phase Funded — TopStep", "Funded", "#3D6EFF"],
            ["22 juin", "Payout reçu — E8 100K", "+$800", teal],
          ].map(([date, label, val, color]) => (
            <div key={date as string} className="flex items-center justify-between rounded bg-muted/30 px-2 py-1.5 text-[10px]">
              <div>
                <span className="text-muted-foreground mr-2">{date as string}</span>
                <span className="font-medium">{label as string}</span>
              </div>
              <span className="font-mono font-semibold shrink-0 ml-2" style={{ color: color as string }}>{val as string}</span>
            </div>
          ))}
        </div>
      </div>
    ),
    // Reporting: card with Mois/Trimestre/Année tabs + table of period rows
    "propfirm-reporting": (
      <div className="rounded-lg border bg-card h-full flex flex-col">
        <div className="flex items-center justify-between px-3 pt-3 pb-2 border-b shrink-0">
          <p className="text-xs font-semibold">Reporting rentabilité</p>
          <div className="flex gap-1">
            {["Mois", "Trimestre", "Année"].map((g, i) => (
              <span key={g} className={`px-2 py-0.5 rounded text-[9px] font-semibold border ${i === 0 ? "border-primary/40 text-primary bg-primary/10" : "border-transparent text-muted-foreground"}`}>{g}</span>
            ))}
          </div>
        </div>
        <div className="overflow-hidden flex-1">
          <table className="w-full text-[9px] font-mono">
            <thead>
              <tr className="border-b bg-muted/30">
                {["Période","Payouts","Frais","Net","ROI"].map(h => (
                  <th key={h} className="px-2 py-1.5 text-left font-semibold text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ["Avr 2026","$800","-$99","$701","+709%"],
                ["Mai 2026","$1 600","-$148","$1 452","+981%"],
                ["Jun 2026","$800","-$99","$701","+709%"],
              ].map(([period, payouts, fees, net, roi]) => (
                <tr key={period} className="border-b">
                  <td className="px-2 py-1.5 text-muted-foreground">{period}</td>
                  <td className="px-2 py-1.5" style={{ color: teal }}>{payouts}</td>
                  <td className="px-2 py-1.5" style={{ color: red }}>{fees}</td>
                  <td className="px-2 py-1.5" style={{ color: teal }}>{net}</td>
                  <td className="px-2 py-1.5" style={{ color: teal }}>{roi}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
    // Accounts widget: card with table — Nom / Statut badge / Coût défi / Payouts reçus
    "propfirm-accounts": (
      <div className="rounded-lg border bg-card h-full flex flex-col">
        <div className="px-3 pt-3 pb-2 border-b shrink-0">
          <p className="text-xs font-semibold">Payouts par compte</p>
        </div>
        <div className="overflow-hidden flex-1 px-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                {["Compte","Statut","Coût défi","Reçu"].map(h => (
                  <th key={h} className="text-left pb-1.5 pr-2 pt-1.5 font-medium text-muted-foreground text-[10px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ["FTMO 50K","pa","-$148","+$1 600",teal,"#00C9A7/40"],
                ["TopStep 100K","challenge","-$99","+$800",gold,"amber-400/40"],
                ["E8 Funding","pa","—","+$800",teal,"#00C9A7/40"],
              ].map(([name, status, cost, received, color]) => (
                <tr key={name as string} className="border-b last:border-b-0">
                  <td className="py-1.5 pr-2 font-medium text-xs max-w-[80px] truncate">{name as string}</td>
                  <td className="py-1.5 pr-2">
                    <span className="border rounded px-1 py-0 text-[10px]" style={{ borderColor: color as string, color: color as string }}>{status as string}</span>
                  </td>
                  <td className="py-1.5 pr-2 font-mono text-[10px]" style={{ color: red }}>{cost as string}</td>
                  <td className="py-1.5 font-mono font-semibold text-[10px]" style={{ color: teal }}>{received as string}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
    // Payouts list: table Date / Account / Amount + certificate badge
    "propfirm-payouts-list": (
      <div className="rounded-lg border bg-card h-full flex flex-col">
        <div className="overflow-hidden flex-1">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/30">
                {["Date","Compte","Montant",""].map((h, i) => (
                  <th key={i} className="text-left px-3 py-2 font-medium text-muted-foreground text-[10px]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ["22 juin", "FTMO 50K", "+$1 600", true],
                ["08 juin", "E8 Funding", "+$800", false],
                ["15 mai", "TopStep 100K", "+$800", true],
              ].map(([date, account, amount, hasCert]) => (
                <tr key={`${date}-${account}`} className="border-b last:border-b-0">
                  <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">{date as string}</td>
                  <td className="px-3 py-2 font-medium text-[10px] truncate max-w-[100px]">{account as string}</td>
                  <td className="px-3 py-2 font-mono font-semibold text-[10px]" style={{ color: teal }}>{amount as string}</td>
                  <td className="px-3 py-2">
                    {hasCert && <span className="border rounded px-1 py-0 text-[9px]" style={{ borderColor: "hsl(var(--primary) / 0.25)", color: teal }}>Certif.</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
    // Filters bar: DateRangePicker + AccountGroupFilter + StrategyMultiSelect
    "propfirm-filters": (
      <div className="rounded-lg border bg-card h-full flex items-center px-3 gap-2">
        <div className="rounded-md border bg-muted/30 px-2.5 py-1.5 text-[10px] text-muted-foreground flex items-center gap-1.5 whitespace-nowrap">
          <span>📅</span><span>01 jan – 31 déc 2026</span>
        </div>
        <div className="rounded-md border bg-muted/30 px-2.5 py-1.5 text-[10px] text-muted-foreground flex items-center gap-1.5 whitespace-nowrap">
          <span>🏢</span><span>Tous les comptes</span>
        </div>
        <div className="rounded-md border bg-muted/30 px-2.5 py-1.5 text-[10px] text-muted-foreground flex items-center gap-1.5 whitespace-nowrap">
          <span>🎯</span><span>Toutes stratégies</span>
        </div>
      </div>
    ),
    // Accounts table: PayoutsAnalyticsSection accountsTable — columns Compte/Statut/Défi/Activation/Frais/Reçu/Net/ROI
    "propfirm-accounts-table": (
      <div className="rounded-lg border bg-card h-full flex flex-col overflow-hidden">
        <div className="px-3 pt-3 pb-2 border-b shrink-0">
          <p className="text-xs font-semibold">Suivi par compte</p>
        </div>
        <div className="overflow-hidden flex-1">
          <table className="w-full font-mono" style={{ fontSize: 9 }}>
            <thead>
              <tr className="border-b bg-muted/30">
                {["Compte","Statut","Défi","Reçu","Net","ROI"].map(h => (
                  <th key={h} className="px-2 py-1.5 text-left font-semibold text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ["FTMO 50K","pa","-$148","+$1 600","+$1 452","+981%",teal,"#00C9A7"],
                ["TopStep 100K","chall.","-$99","+$800","+$701","+709%",gold,"#F5C842"],
                ["E8 Funding","pa","—","+$800","+$800","—",teal,"#00C9A7"],
              ].map(([name, status, cost, received, net, roi, sc, rc]) => (
                <tr key={name as string} className="border-b last:border-b-0">
                  <td className="px-2 py-1.5 max-w-[70px] truncate font-sans font-medium" style={{ fontSize: 9 }}>{name as string}</td>
                  <td className="px-2 py-1.5"><span className="border rounded px-1" style={{ borderColor: `${sc}60`, color: sc as string }}>{status as string}</span></td>
                  <td className="px-2 py-1.5" style={{ color: red }}>{cost as string}</td>
                  <td className="px-2 py-1.5" style={{ color: teal }}>{received as string}</td>
                  <td className="px-2 py-1.5" style={{ color: teal }}>{net as string}</td>
                  <td className="px-2 py-1.5" style={{ color: rc as string }}>{roi as string}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
    // Global charges: PayoutsAnalyticsSection globalCharges — list of charges with total
    "propfirm-global-charges": (
      <div className="rounded-lg border bg-card h-full flex flex-col">
        <div className="px-3 pt-3 pb-2 border-b shrink-0">
          <p className="text-xs font-semibold">Frais globaux</p>
        </div>
        <div className="flex flex-col gap-1.5 p-3 overflow-hidden">
          {[
            ["Abonnement mensuel FTMO", "monthly", "-$99"],
            ["Activation E8 Funding", "one_time", "-$49"],
          ].map(([name, freq, amt]) => (
            <div key={name as string} className="flex items-center justify-between rounded-lg border bg-muted/20 px-2.5 py-2">
              <div>
                <p className="text-[10px] font-medium">{name as string}</p>
                <p className="text-[9px] text-muted-foreground">{freq as string}</p>
              </div>
              <span className="text-[10px] font-mono font-semibold" style={{ color: red }}>{amt as string}</span>
            </div>
          ))}
          <div className="flex justify-between text-[10px] font-semibold pt-1 border-t mt-1">
            <span className="text-muted-foreground">Total</span>
            <span className="font-mono" style={{ color: red }}>-$148</span>
          </div>
        </div>
      </div>
    ),
    // Wallet balance: big available balance + tree breakdown (initial / transfers / withdrawals)
    "propfirm-wallet-balance": (
      <div className="h-full flex flex-col gap-2">
        <div className="rounded-lg border bg-card p-4 flex-1 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span style={{ color: teal }}>⬡</span>
              <span className="font-semibold text-sm">Portefeuille</span>
            </div>
            <div className="flex gap-1">
              <div className="w-6 h-6 rounded border bg-muted/30" />
              <div className="w-14 h-6 rounded border bg-muted/30" />
            </div>
          </div>
          <div className="flex justify-between items-baseline mb-3">
            <span className="text-2xl font-bold font-mono" style={{ color: teal }}>$4,200.00</span>
            <span className="text-[10px] text-muted-foreground">Solde disponible</span>
          </div>
          <div className="text-[10px] text-muted-foreground space-y-0.5 pt-2 border-t border-border/50 font-mono">
            <div className="flex justify-between"><span>├─ Solde initial</span><span>$0.00</span></div>
            <div className="flex justify-between"><span>├─ Transferts reçus (4)</span><span style={{ color: "hsl(var(--edgely-pnl-positive))" }}>+$5,600.00</span></div>
            <div className="flex justify-between"><span>└─ Retraits (2)</span><span style={{ color: "hsl(var(--edgely-pnl-negative))" }}>-$1,400.00</span></div>
          </div>
        </div>
      </div>
    ),
    // Wallet transfers: card with title + table of transfers
    "propfirm-wallet-transfers": (
      <div className="rounded-lg border bg-card h-full flex flex-col p-4">
        <h3 className="text-sm font-semibold mb-3 shrink-0">Transferts reçus</h3>
        <div className="flex-1 overflow-hidden">
          <table className="w-full text-[10px] font-mono">
            <thead>
              <tr className="border-b">
                {["Compte","Date","Montant","Statut"].map(h => (
                  <th key={h} className="text-left pb-1.5 font-medium text-muted-foreground font-sans">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ["FTMO 50K", "22 juin 2026", "+$1 600", "Approuvé", teal],
                ["E8 Funding", "08 juin 2026", "+$800", "Approuvé", teal],
                ["TopStep 100K", "15 mai 2026", "+$800", "Approuvé", teal],
              ].map(([account, date, amount, status, color]) => (
                <tr key={`${account}-${date}`} className="border-b last:border-b-0">
                  <td className="py-1.5 pr-2 font-sans font-medium truncate max-w-[80px]">{account as string}</td>
                  <td className="py-1.5 pr-2 text-muted-foreground">{date as string}</td>
                  <td className="py-1.5 pr-2" style={{ color: color as string }}>{amount as string}</td>
                  <td className="py-1.5"><span className="border rounded px-1 text-[9px]" style={{ borderColor: `${color}50`, color: color as string }}>{status as string}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
    // Wallet withdrawals: card with title + table of withdrawals
    "propfirm-wallet-withdrawals": (
      <div className="rounded-lg border bg-card h-full flex flex-col p-4">
        <h3 className="text-sm font-semibold mb-3 shrink-0">Retraits effectués</h3>
        <div className="flex-1 overflow-hidden">
          <table className="w-full text-[10px] font-mono">
            <thead>
              <tr className="border-b">
                {["Date","Montant","Statut","Notes"].map(h => (
                  <th key={h} className="text-left pb-1.5 font-medium text-muted-foreground font-sans">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ["10 juin 2026", "-$1 200", "Complété"],
                ["28 avr 2026", "-$800", "Complété"],
              ].map(([date, amount, status]) => (
                <tr key={`${date}`} className="border-b last:border-b-0">
                  <td className="py-1.5 pr-2 text-muted-foreground">{date as string}</td>
                  <td className="py-1.5 pr-2" style={{ color: teal }}>{amount as string}</td>
                  <td className="py-1.5 pr-2"><span className="border rounded px-1 text-[9px]" style={{ borderColor: `${teal}50`, color: teal }}>{status as string}</span></td>
                  <td className="py-1.5 text-muted-foreground">—</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
    "propfirm-upcoming-charges": <UpcomingChargesPreview />,
  };

  const preview = content[widgetKey];
  return (
    <div className="overflow-hidden rounded-lg" style={{ height, minWidth: 200 }}>
      {preview ?? (
        <div className="flex items-center justify-center h-full rounded-lg border bg-muted/30 text-sm text-muted-foreground">
          Aperçu non disponible
        </div>
      )}
    </div>
  );
}

/** Aperçu du widget « Prélèvements à venir » : libellés traduits, données fictives. */
function UpcomingChargesPreview() {
  const u = useTranslation().lite.pfm.upcoming;
  const rows: [string, string][] = [["Apex 50K", "-$85"], ["Topstep 100K", "-$165"], ["TradingView", "-$30"]];
  return (
    <div className="rounded-lg border bg-card h-full flex flex-col">
      <div className="px-3 pt-3 pb-2 border-b shrink-0">
        <p className="text-xs font-semibold">{u.title}</p>
      </div>
      <div className="flex flex-col gap-1.5 p-3 overflow-hidden">
        <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{u.toConfirmTitle}</p>
        {rows.map(([name, amt], i) => (
          <div key={name} className="flex items-center justify-between rounded-lg border px-2.5 py-2">
            <p className="text-[10px] font-medium">{name}</p>
            <div className="flex items-center gap-2">
              {i === 0 && <span className="text-[9px] rounded border px-1.5 py-0.5">{u.paid}</span>}
              <span className="text-[10px] font-mono font-semibold text-pnlNegative">{amt}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Natural widget width assuming a full 6-column grid (~800px typical)
const NATURAL_W = 800;
const MAX_PREVIEW_W = 460;
const MAX_PREVIEW_H = 320;

function WidgetPreview({ widgetKey, t, isDark }: { widgetKey: WidgetKey; t: any; isDark: boolean }) {
  const reg = WIDGET_REGISTRY[widgetKey];
  const naturalH = reg.minRowSpan * reg.rowHeight;
  // Scale uniformly so the widget fits both width and height constraints
  const scaleW = MAX_PREVIEW_W / NATURAL_W;
  const scaleH = naturalH > MAX_PREVIEW_H ? MAX_PREVIEW_H / naturalH : 1;
  const scale = Math.min(scaleW, scaleH);
  const outerW = Math.round(NATURAL_W * scale);
  const outerH = Math.round(naturalH * scale);

  if (TOPBAR_WIDGET_KEYS.has(widgetKey)) {
    const iconMap: Record<string, string> = {
      "topbar-date-filter": "📅",
      "topbar-account-filter": "🏦",
      "topbar-strategy-filter": "🎯",
      "topbar-add-trade": "+ Trade",
      "topbar-add-payout": "+ Payout",
      "topbar-pfm-filter": "🏢",
      "topbar-phase-filter": "🎯",
      "topbar-symbol-filter": "🔍",
      "topbar-side-filter": "↕",
      "topbar-status-filter": "◐",
      "topbar-pnl-filter": "💲",
      "topbar-tags-filter": "🏷",
    };
    return (
      <div
        className="flex items-center justify-center rounded-lg border bg-muted/30 px-6 py-4 gap-2 text-sm font-medium"
        style={{ minHeight: 60, minWidth: 200 }}
      >
        <span className="text-lg">{iconMap[widgetKey]}</span>
        <span className="text-muted-foreground">Élément de topbar</span>
      </div>
    );
  }

  if (PFM_WIDGET_KEYS.has(widgetKey)) {
    return <PropFirmWidgetPreview widgetKey={widgetKey} height={outerH} />;
  }

  if (widgetKey === "note") {
    return (
      <div className="rounded-lg border bg-card p-3 overflow-hidden" style={{ height: outerH }}>
        <p className="text-xs text-muted-foreground font-medium mb-1">Ma note</p>
        <p className="text-sm text-muted-foreground">Vos observations...</p>
      </div>
    );
  }

  if (widgetKey === "mentor-notes") {
    return (
      <div className="rounded-lg border bg-card p-3 overflow-hidden flex flex-col gap-2" style={{ height: outerH }}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-primary">Mentor Program</span>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-primary">John D.</span>
            <span className="text-[10px] text-muted-foreground">il y a 2 jours</span>
          </div>
          <p className="text-xs text-muted-foreground">Bon travail cette semaine, continue à respecter ton plan de trading.</p>
        </div>
      </div>
    );
  }

  if (widgetKey === "chart") {
    const teal = "hsl(var(--primary))";
    const up = "hsl(var(--chart-candle-up))";
    const down = "hsl(var(--chart-candle-down))";
    const bars = [0.6, 0.8, 0.5, 0.9, 0.4, 0.7, 1.0, 0.75, 0.55, 0.85];
    return (
      <div className="rounded-lg border bg-card overflow-hidden flex flex-col" style={{ height: outerH }}>
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-border shrink-0 flex-wrap">
          <span className="text-[10px] font-mono bg-accent/40 border border-border rounded px-1.5 py-0.5">EURUSD</span>
          {["1m","1h","1d"].map((tf) => (
            <span key={tf} className={`text-[10px] px-1.5 py-0.5 rounded ${tf === "1h" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{tf}</span>
          ))}
          <span className="ml-auto text-[10px] text-muted-foreground flex items-center gap-0.5">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><line x1="12" y1="3" x2="12" y2="21"/><line x1="3" y1="12" x2="21" y2="12"/></svg>
            Trades
          </span>
        </div>
        <div className="flex-1 flex items-end gap-0.5 px-2 pb-2 pt-1">
          {bars.map((h, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
              <div className="w-0.5 mx-auto" style={{ height: `${(1 - h) * 40}%`, background: i % 3 === 0 ? down : up }} />
              <div className="w-full rounded-sm" style={{ height: `${h * 50}%`, background: i % 3 === 0 ? down : up }} />
            </div>
          ))}
        </div>
        <div className="px-2 pb-1 flex items-center gap-1">
          <svg viewBox="0 0 24 24" width="8" height="8" fill="none" stroke={teal} strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/></svg>
          <div className="h-px flex-1" style={{ background: `${teal}40` }} />
        </div>
      </div>
    );
  }

  const WidgetComp = WIDGET_COMPONENTS[widgetKey];
  if (!WidgetComp) return (
    <div className="flex items-center justify-center rounded-lg border bg-muted/30 text-sm text-muted-foreground" style={{ height: outerH }}>
      Aperçu non disponible
    </div>
  );

  // Header ~44px + padding ~20px = 64px overhead; chart height = remaining space in natural dimensions
  const chartH = Math.max(80, naturalH - 64);
  const mockData = makeMockData(t, isDark, chartH);

  return (
    <div
      className="rounded-lg border bg-card"
      style={{ height: outerH, width: outerW, position: "relative", overflow: "hidden", flexShrink: 0 }}
    >
      <DashboardDataProvider value={mockData as any}>
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            width: NATURAL_W,
            height: naturalH,
            pointerEvents: "none",
          }}
        >
          <WidgetComp />
        </div>
      </DashboardDataProvider>
    </div>
  );
}

export default function AddWidgetPicker({
  open,
  onOpenChange,
  layout,
  maxColSpan,
  rowStart: _rowStart,
  colStart: _colStart,
  scope = "dashboard",
  initialCategory,
  onPick,
  onUpgradeClick,
  isFreePlan = false,
  hasMentor = false,
  topbarPinState,
}: AddWidgetPickerProps) {
  const t = useTranslation();
  const isDark = useIsDark();

  const scopedCategories = WIDGET_CATEGORIES.filter((cat) => {
    if (cat.key === "mentorProgram") return !!hasMentor;
    return true;
  });

  const defaultCategory = initialCategory ?? (scope === "custom-page" ? "topbar" : (scopedCategories[0]?.key ?? "dashboard")) as WidgetCategoryKey;

  const [activeCategory, setActiveCategory] = useState<WidgetCategoryKey>(defaultCategory);
  const [selectedKey, setSelectedKey] = useState<WidgetKey | null>(null);
  // true once the user has explicitly clicked a widget (vs just hovered)
  const [selectionLocked, setSelectionLocked] = useState(false);

  // Reset active category when the picker opens or scope/initialCategory changes
  useEffect(() => {
    if (!open) return;
    setActiveCategory(initialCategory ?? (scope === "custom-page" ? "topbar" : (scopedCategories[0]?.key ?? "dashboard")) as WidgetCategoryKey);
    setSelectedKey(null);
    setSelectionLocked(false);
  }, [open, scope, initialCategory]);

  const placedKeys = useMemo(() => {
    const keys = new Set<WidgetKey>();
    for (const slot of layout.slots) {
      if (slot.widgetKey !== "note") keys.add(slot.widgetKey);
    }
    return keys;
  }, [layout]);

  const activeCategoryObj = scopedCategories.find((c) => c.key === activeCategory) ?? scopedCategories[0];
  const widgetsInCategory = activeCategoryObj?.widgets ?? [];

  const isIncompatible = (key: WidgetKey) => WIDGET_REGISTRY[key].minColSpan > maxColSpan;

  const firstCompatible = widgetsInCategory.find((k) => !isIncompatible(k)) ?? widgetsInCategory[0] ?? null;
  const effectiveSelected = selectedKey ?? firstCompatible;
  const selectedReg = effectiveSelected ? WIDGET_REGISTRY[effectiveSelected] : null;
  const selectedIncompatible = effectiveSelected ? isIncompatible(effectiveSelected) : false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle>{t.dashboard.addWidget}</DialogTitle>
        </DialogHeader>
        <div className="flex h-[500px]">
          {/* Col 1: categories — 160px */}
          <div className="w-40 border-r flex flex-col py-2 shrink-0 overflow-y-auto">
            {scopedCategories.map((cat) => {
              const label = t.dashboard.widgetCategories[cat.key as WidgetCategoryKey];
              const parts = label.split(" — ");
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => {
                    setActiveCategory(cat.key as WidgetCategoryKey);
                    setSelectedKey(null);
                    setSelectionLocked(false);
                  }}
                  className={cn(
                    "text-left px-4 py-2.5 text-sm leading-tight transition-colors",
                    "hover:bg-muted/50",
                    activeCategory === cat.key && "bg-primary/10 text-primary font-medium"
                  )}
                >
                  {parts.length > 1 ? (
                    <>
                      <span className="block">{parts[0]}</span>
                      <span className="block text-xs opacity-75">{parts[1]}</span>
                    </>
                  ) : label}
                </button>
              );
            })}
          </div>

          {/* Col 2: widget list — 190px */}
          <div className="w-[190px] border-r overflow-y-auto py-2 shrink-0">
            {widgetsInCategory.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted-foreground">Aucun widget compatible</p>
            ) : (
              widgetsInCategory.map((key) => {
                const isPlaced = placedKeys.has(key);
                const incompatible = isIncompatible(key);
                const isComingSoon = key === 'earnings-calendar';
                const locked = isWidgetLocked(key, isFreePlan ?? false);
                const camelKey = camelize(key) as keyof typeof t.dashboard.widgets;
                const isTopbar = TOPBAR_WIDGET_KEYS.has(key);
                const pinValue = isTopbar ? topbarPinState?.values[key] : undefined;
                const pinDefault = isTopbar ? topbarPinState?.defaults[key] : undefined;
                const isPinned = isTopbar && !!pinValue && pinValue === pinDefault;
                const disabled = incompatible || isComingSoon;
                return (
                  <div key={key} className="flex items-center pr-2">
                    <button
                      type="button"
                      disabled={disabled}
                      onMouseEnter={() => {
                        if (!disabled && !locked && !selectionLocked) setSelectedKey(key);
                      }}
                      onClick={() => {
                        if (locked) {
                          onUpgradeClick?.();
                          onOpenChange(false);
                        } else if (!disabled) {
                          setSelectedKey(key);
                          setSelectionLocked(true);
                        }
                      }}
                      className={cn(
                        "flex-1 text-left px-4 py-2.5 text-sm transition-colors",
                        !disabled && "hover:bg-muted/50",
                        isPlaced && !isComingSoon && !locked && "opacity-40",
                        incompatible && !isComingSoon && "opacity-40 cursor-not-allowed",
                        isComingSoon && "text-muted-foreground/40 cursor-not-allowed",
                        effectiveSelected === key && !disabled && !locked && "bg-primary/10 text-primary"
                      )}
                    >
                      <span className="flex items-center gap-1.5 truncate">
                        <span className="truncate">{t.dashboard.widgets[camelKey]}</span>
                        {locked && (
                          <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30 shrink-0">
                            Pro
                          </span>
                        )}
                        {locked && (
                          <Lock className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                        )}
                      </span>
                      {isComingSoon && (
                        <span className="inline-block text-[8px] font-semibold tracking-wide uppercase rounded px-1.5 py-0.5 mt-0.5 leading-none bg-muted text-muted-foreground">
                          Soon
                        </span>
                      )}
                      {isPlaced && !disabled && !locked && (
                        <span className="block text-[10px] text-muted-foreground">Déjà ajouté</span>
                      )}
                      {incompatible && !isComingSoon && (
                        <span className="block text-[10px] text-muted-foreground">Min {WIDGET_REGISTRY[key].minColSpan} col</span>
                      )}
                    </button>
                    {isTopbar && isPlaced && topbarPinState && pinValue && (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                topbarPinState.onPin(key, pinValue);
                              }}
                              className={cn(
                                "shrink-0 flex h-6 w-6 items-center justify-center rounded transition-colors",
                                isPinned
                                  ? "text-yellow-400 hover:text-yellow-500"
                                  : "text-muted-foreground/30 hover:text-yellow-400"
                              )}
                            >
                              <Star className={cn("h-3.5 w-3.5", isPinned && "fill-yellow-400")} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="text-xs">
                            {isPinned ? t.pages.pinAsDefaultConfirm : t.pages.pinAsDefault}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Col 3: preview — flex-1 */}
          <div className="flex-1 flex flex-col p-4 gap-3 overflow-hidden min-w-0">
            {effectiveSelected && selectedReg ? (
              <>
                <div className="shrink-0 flex justify-center">
                  <WidgetPreview widgetKey={effectiveSelected} t={t} isDark={isDark} />
                </div>
                <div>
                  <p className="font-medium text-sm">
                    {t.dashboard.widgets[camelize(effectiveSelected) as keyof typeof t.dashboard.widgets]}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {selectedReg.minColSpan}–{selectedReg.maxColSpan} col · {selectedReg.minRowSpan}–{selectedReg.maxRowSpan} lignes
                  </p>
                </div>
                {isWidgetLocked(effectiveSelected, isFreePlan ?? false) ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full gap-1.5"
                    onClick={() => { onUpgradeClick?.(); onOpenChange(false); }}
                  >
                    <Lock className="h-3.5 w-3.5" />
                    <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30">Pro</span>
                    {t.account.upgradeToPro}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={selectedIncompatible}
                    onClick={() => { if (!selectedIncompatible) { onPick(effectiveSelected); onOpenChange(false); } }}
                  >
                    {t.dashboard.addWidget ?? "Ajouter ce widget"}
                  </Button>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                Sélectionner un widget
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
